import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlannedStint,
  SharedAccount,
  StrategyConfig,
  StrategyPlan,
  StrategySummary,
} from '../types';
import {
  EventChoice,
  StrategyConflictError,
  createStrategy,
  deleteStrategy,
  fetchStrategy,
  listEventChoices,
  listStrategies,
  resetStrategyActuals,
  saveStrategy,
} from '../auth/api';
import { PIT_MARGIN_MINUTES, deriveConstraints } from '../analytics/regulations';
import {
  generatePlan,
  reflowStints,
  summarisePlan,
  validatePlan,
  withStopKinds,
  Violation,
  PlanSummary,
} from '../analytics/stintPlan';

/** Unsaved plans carry this id so `save` knows to POST rather than PUT. */
export const DRAFT_ID = '';

/**
 * A sensible blank sheet: the most common format, starting at the next round
 * 10:00. `now` is injected so tests do not depend on the wall clock.
 */
export function emptyPlan(now: number = Date.now()): StrategyPlan {
  const start = new Date(now);
  start.setHours(10, 0, 0, 0);
  return {
    id: DRAFT_ID,
    title: 'New strategy',
    config: {
      durationHours: 12,
      category: 'PRO',
      trackId: 'haskovo',
      startsAt: start.toISOString(),
      requiredStopsOverride: null,
      directionChange: true,
      stintBias: 'even',
      maximiseStintTime: false,
      preferDoubleStints: false,
      pitMarginMinutes: PIT_MARGIN_MINUTES,
    },
    roster: [],
    stints: [],
    eventId: null,
    teamId: null,
    kartNumber: null,
    actuals: {},
    ownerId: null,
    ownerName: null,
    sharedWith: [],
    updatedAt: new Date(now).toISOString(),
  };
}

export interface UseStrategyPlan {
  plan: StrategyPlan;
  saved: StrategySummary[];
  events: EventChoice[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  dirty: boolean;
  /** The stored copy, when somebody else saved after this one was opened. */
  conflict: StrategyPlan | null;
  violations: Violation[];
  summary: PlanSummary;
  setTitle: (title: string) => void;
  setEvent: (eventId: string | null) => void;
  setTeam: (teamId: string | null, kartNumber: string | null) => void;
  setSharedWith: (accounts: SharedAccount[]) => void;
  setConfig: (patch: Partial<StrategyConfig>) => void;
  addDriver: (name: string) => void;
  removeDriver: (index: number) => void;
  toggleAvailable: (index: number) => void;
  moveDriver: (index: number, delta: number) => void;
  generate: () => void;
  setStint: (index: number, patch: Partial<PlannedStint>) => void;
  save: () => Promise<void>;
  load: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  startNew: () => void;
  /** Drop my changes and show what the other person saved. */
  acceptTheirs: () => void;
  /** Save mine over what the other person saved. */
  keepMine: () => Promise<void>;
  /** Forget everything the live race recorded against this plan. */
  resetActuals: () => Promise<void>;
  dismissError: () => void;
}

function message(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export function useStrategyPlan(now?: number): UseStrategyPlan {
  const [plan, setPlan] = useState<StrategyPlan>(() => emptyPlan(now));
  const [saved, setSaved] = useState<StrategySummary[]>([]);
  const [events, setEvents] = useState<EventChoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState<StrategyPlan | null>(null);

  const refreshList = useCallback(async () => {
    try {
      setSaved(await listStrategies());
    } catch (err) {
      setError(message(err, 'Could not list saved strategies'));
    }
  }, []);

  // /strategy?id=… opens that plan, which is how the live panel links back here.
  useEffect(() => {
    let id: string | null = null;
    try {
      id = new URLSearchParams(window.location.search).get('id');
    } catch {
      // No window to read (or a malformed URL): start from a blank sheet.
    }
    if (id) void load(id);
    // Only on first render: later changes are the user's, not the address bar's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void refreshList();
    // A missing event list only costs the attach picker, so it must not surface
    // as an error over the strategy itself.
    listEventChoices()
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [refreshList]);

  /** Every mutator goes through here so `dirty` cannot be forgotten. */
  const edit = useCallback((fn: (p: StrategyPlan) => StrategyPlan) => {
    setPlan((prev) => fn(prev));
    setDirty(true);
  }, []);

  const setTitle = useCallback(
    (title: string) => edit((p) => ({ ...p, title })),
    [edit],
  );

  const setEvent = useCallback(
    (eventId: string | null) =>
      // A grid row only means something inside the race it came from, so moving
      // the plan to another race drops it. The kart number stays as a hint.
      edit((p) => ({ ...p, eventId, teamId: eventId === p.eventId ? p.teamId : null })),
    [edit],
  );

  const setTeam = useCallback(
    (teamId: string | null, kartNumber: string | null) =>
      edit((p) => ({ ...p, teamId, kartNumber })),
    [edit],
  );

  const setSharedWith = useCallback(
    (accounts: SharedAccount[]) => edit((p) => ({ ...p, sharedWith: accounts })),
    [edit],
  );

  const setConfig = useCallback(
    (patch: Partial<StrategyConfig>) =>
      edit((p) => ({ ...p, config: { ...p.config, ...patch } })),
    [edit],
  );

  const addDriver = useCallback(
    (name: string) =>
      edit((p) => {
        const trimmed = name.trim();
        if (!trimmed) return p;
        // A repeated name would double-count that driver's time and make the
        // running order ambiguous — the backend rejects it too.
        if (p.roster.some((d) => d.name.toLowerCase() === trimmed.toLowerCase())) {
          return p;
        }
        return { ...p, roster: [...p.roster, { name: trimmed, unavailable: false }] };
      }),
    [edit],
  );

  const removeDriver = useCallback(
    (index: number) =>
      edit((p) => {
        const roster = p.roster.filter((_, i) => i !== index);
        // Stints point at roster positions, so removing a driver renumbers
        // everyone after them. Re-point rather than leave dangling indexes.
        const stints = p.stints.map((s) => {
          if (s.driverIndex === null) return s;
          if (s.driverIndex === index) return { ...s, driverIndex: null };
          return s.driverIndex > index
            ? { ...s, driverIndex: s.driverIndex - 1 }
            : s;
        });
        return { ...p, roster, stints: withStopKinds(stints, p.config, roster) };
      }),
    [edit],
  );

  const toggleAvailable = useCallback(
    (index: number) =>
      edit((p) => ({
        ...p,
        roster: p.roster.map((d, i) =>
          i === index ? { ...d, unavailable: !d.unavailable } : d,
        ),
      })),
    [edit],
  );

  const moveDriver = useCallback(
    (index: number, delta: number) =>
      edit((p) => {
        const target = index + delta;
        if (target < 0 || target >= p.roster.length) return p;
        const roster = [...p.roster];
        [roster[index], roster[target]] = [roster[target], roster[index]];
        // Swapping roster rows would otherwise silently reassign stints.
        const swap = (d: number | null): number | null =>
          d === index ? target : d === target ? index : d;
        return {
          ...p,
          roster,
          stints: p.stints.map((s) => ({ ...s, driverIndex: swap(s.driverIndex) })),
        };
      }),
    [edit],
  );

  const generate = useCallback(
    () =>
      edit((p) => ({ ...p, stints: generatePlan(p.config, p.roster).stints })),
    [edit],
  );

  const setStint = useCallback(
    (index: number, patch: Partial<PlannedStint>) =>
      edit((p) => {
        // Typing a length is a decision: pin it, so a later live re-plan spreads
        // a long stint over the others rather than rescaling this one.
        const applied: Partial<PlannedStint> =
          patch.minutes !== undefined && patch.pinned === undefined
            ? { ...patch, pinned: true }
            : patch;
        const stints = p.stints.map((s, i) => (i === index ? { ...s, ...applied } : s));
        // A length change moves everything after it, so the sheet is re-timed
        // rather than left showing start times that no longer follow on.
        const minPit = deriveConstraints(p.config, p.roster.length).minPitMinutes;
        // A different driver can turn a kart-only stop into a driver change (or
        // back), and the totals on the sheet count them, so recompute the kinds.
        return {
          ...p,
          stints: withStopKinds(reflowStints(stints, minPit), p.config, p.roster),
        };
      }),
    [edit],
  );

  const persist = useCallback(
    async (target: StrategyPlan) => {
      setSaving(true);
      setError(null);
      setConflict(null);
      try {
        const input = {
          title: target.title,
          config: target.config,
          roster: target.roster,
          stints: target.stints,
          eventId: target.eventId,
          teamId: target.teamId,
          kartNumber: target.kartNumber,
          sharedWith: target.sharedWith.map((a) => a.id),
          // Without it, saving would silently overwrite a colleague's edit.
          expectedUpdatedAt: target.id ? target.updatedAt : null,
        };
        const stored = target.id
          ? await saveStrategy(target.id, input)
          : await createStrategy(input);
        setPlan(stored);
        setDirty(false);
        await refreshList();
      } catch (err) {
        if (err instanceof StrategyConflictError) {
          setConflict(err.current);
          setError(
            'Someone else saved this strategy after you opened it. ' +
              'Load their version, or keep yours and save over it.',
          );
        } else {
          setError(message(err, 'Could not save the strategy'));
        }
      } finally {
        setSaving(false);
      }
    },
    [refreshList],
  );

  const save = useCallback(() => persist(plan), [persist, plan]);

  const acceptTheirs = useCallback(() => {
    if (!conflict) return;
    setPlan(conflict);
    setDirty(false);
    setConflict(null);
    setError(null);
  }, [conflict]);

  const keepMine = useCallback(async () => {
    if (!conflict) return;
    // Re-based on what is stored now, so the server sees an up-to-date edit.
    await persist({ ...plan, updatedAt: conflict.updatedAt });
  }, [conflict, persist, plan]);

  const resetActuals = useCallback(async () => {
    if (!plan.id) return;
    setError(null);
    try {
      const cleared = await resetStrategyActuals(plan.id);
      setPlan((prev) => ({ ...prev, actuals: cleared.actuals }));
    } catch (err) {
      setError(message(err, 'Could not reset the recorded stints'));
    }
  }, [plan.id]);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      setPlan(await fetchStrategy(id));
      setDirty(false);
      setConflict(null);
    } catch (err) {
      setError(message(err, 'Could not open that strategy'));
    } finally {
      setLoading(false);
    }
  }, []);

  const remove = useCallback(
    async (id: string) => {
      setError(null);
      try {
        await deleteStrategy(id);
        // Deleting the plan on screen leaves the editor showing something that
        // no longer exists; drop back to a blank sheet.
        setPlan((prev) => (prev.id === id ? emptyPlan(now) : prev));
        await refreshList();
      } catch (err) {
        setError(message(err, 'Could not delete that strategy'));
      }
    },
    [refreshList, now],
  );

  const startNew = useCallback(() => {
    setPlan(emptyPlan(now));
    setDirty(false);
    setConflict(null);
  }, [now]);

  const violations = useMemo(() => validatePlan(plan), [plan]);
  const summary = useMemo(() => summarisePlan(plan), [plan]);

  return {
    plan,
    saved,
    events,
    loading,
    saving,
    error,
    dirty,
    conflict,
    violations,
    summary,
    setTitle,
    setEvent,
    setTeam,
    setSharedWith,
    setConfig,
    addDriver,
    removeDriver,
    toggleAvailable,
    moveDriver,
    generate,
    setStint,
    save,
    load,
    remove,
    startNew,
    acceptTheirs,
    keepMine,
    resetActuals,
    dismissError: () => setError(null),
  };
}
